class InfiniteScroll extends HTMLElement {
  constructor() {
    super();
    this.productsContainerSelector = this.getAttribute('data-products-container');
    this.paginationSelector = this.getAttribute('data-pagination-selector');
    this.productsContainerEl = this.querySelector(this.productsContainerSelector);
    this.paginationEl = this.querySelector(this.paginationSelector);
    this.loadingIndicator = this.querySelector('.loading-overlay-wrapper');
    this.currentPage = parseInt(new URL(window.location.href).searchParams.get('page') || '1');

    if (!this.productsContainerEl || !this.paginationEl) {
      console.error('Wrong configuration for infinite scroll');
      return;
    }

    try {
      this.observer = this.registerObserver(this.loadNextPage.bind(this));
      this.observer.observe(this.paginationEl);
      this.paginationEl.style.visibility = 'hidden';
    } catch (error) {}
  }

  registerObserver(callback) {
    let callbackIsRunning = false;
    return new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting && !callbackIsRunning) {
        callbackIsRunning = true;
        callback()
          .then(_ => callbackIsRunning = false)
          .catch(_ => callbackIsRunning = false);
      }
    });
  }

  loadNextPage() {
    const allPageLinks = [...this.paginationEl.querySelectorAll('a[href*="page="]')];
    const nextPageEl = allPageLinks.find(a => {
      const url = new URL(a.href);
      return parseInt(url.searchParams.get('page')) === this.currentPage + 1;
    });

    if (!nextPageEl) {
      this.observer.disconnect();
      return Promise.resolve();
    }

    const nextPageUrl = nextPageEl.getAttribute('href');
    this.loadingIndicator.classList.remove('hidden');

    return fetch(nextPageUrl)
      .then(result => {
        this.loadingIndicator.classList.add('hidden');
        if (!result.ok) throw result.statusText;
        return result.text();
      })
      .then(this.handleNextPage.bind(this));
  }

  handleNextPage(nextPageContent) {
    const nextPageHtml = new DOMParser().parseFromString(nextPageContent, 'text/html');
    const nextPageProducts = nextPageHtml.querySelector(this.productsContainerSelector);
    if (!nextPageProducts) return;

    const newCount = nextPageProducts.querySelectorAll('.grid__item').length;
    const productList = this.querySelector('#product-grid');
    const newList = nextPageHtml.querySelector('#product-grid');
    if (!productList || !newList) return;

    // Append new items to the DOM
    newList.querySelectorAll('li').forEach(li => {
      productList.insertAdjacentHTML('beforeend', li.outerHTML);
    });

    this.currentPage += 1;

    // Grab the newly added items
    const allItems = productList.querySelectorAll('.grid__item');
    const newItems = [...allItems].slice(-newCount);

    // Re-initialize Dawn's scroll-trigger system on the new items
    this.reinitScrollTriggers(newItems);

    // Update pagination
    const nextPagePagination = nextPageHtml.querySelector(this.paginationSelector);
    if (!nextPagePagination) {
      this.observer.disconnect();
      return;
    }
    this.paginationEl.innerHTML = nextPagePagination.innerHTML;
  }

  reinitScrollTriggers(newItems) {
    // Reset animation-order so stagger starts from 1 for each new batch
    newItems.forEach((el, i) => {
      el.style.setProperty('--animation-order', (i % 4) + 1);

      // Remove any leftover inline styles from previous custom animation attempts
      el.style.removeProperty('opacity');
      el.style.removeProperty('transform');
      el.style.removeProperty('transition');

      // Ensure Dawn's animation classes are present (they come from Liquid,
      // but just in case they were stripped during innerHTML insertion)
      if (!el.classList.contains('scroll-trigger')) {
        el.classList.add('scroll-trigger');
      }
      if (!el.classList.contains('animate--slide-in')) {
        el.classList.add('animate--slide-in');
      }

      // Mark as offscreen so Dawn's CSS hides it (opacity: 0.01 + translateY)
      el.classList.add('scroll-trigger--offscreen');

      // Remove any "already animated" state from a previous session
      el.classList.remove('scroll-trigger--cancel');
    });

    // Use IntersectionObserver to mimic exactly what Dawn's motion.js does:
    // remove scroll-trigger--offscreen when the element enters the viewport,
    // which lets the CSS animation (slideIn keyframe) play naturally.
    const viewportObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const el = entry.target;
          // Small delay lets the browser paint the hidden state first,
          // preventing items already in view from popping in without animation
          requestAnimationFrame(() => {
            el.classList.remove('scroll-trigger--offscreen');
          });
          viewportObserver.unobserve(el);
        }
      });
    }, {
      // Match Dawn's default: trigger slightly before the item fully enters
      rootMargin: '0px 0px -50px 0px',
      threshold: 0.1
    });

    newItems.forEach(el => viewportObserver.observe(el));
  }
}

customElements.define('infinite-scroll', InfiniteScroll);